import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CaretDown,
  CaretUp,
  Check,
  File,
  Minus,
  Sun
} from '@phosphor-icons/react';
import * as PhosphorIcons from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { getAllLineItemServiceTypes } from '../../constants/lineItemServiceTypes';
import { LIBRARY_GROUP_ORDER } from '../../utils/librarySearch';

const CONTENT_TYPES = [
  {
    code: 'day',
    translationKey: 'daysDisplay',
    fallbackLabel: 'Days',
    Icon: Sun
  },
  {
    code: 'additional',
    translationKey: 'additionalContent',
    fallbackLabel: 'Additional Content',
    Icon: File
  }
];

const SERVICE_TYPES = getAllLineItemServiceTypes();
const SERVICE_TYPE_CODES = SERVICE_TYPES.map(({ code }) => code);

const FilterCheckbox = ({ checked, indeterminate = false }) => (
  <span
    className={`library-type-filter-menu__checkbox${checked || indeterminate ? ' library-type-filter-menu__checkbox--checked' : ''}`}
    aria-hidden='true'
  >
    {indeterminate ? (
      <Minus size={12} weight='bold' />
    ) : (
      checked && <Check size={12} weight='bold' />
    )}
  </span>
);

FilterCheckbox.propTypes = {
  checked: PropTypes.bool.isRequired,
  indeterminate: PropTypes.bool
};

const LibraryTypeFilter = ({ counts, selectedTypes, onChange }) => {
  const { t } = useTranslation('library');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const selectedServiceCount = SERVICE_TYPE_CODES.filter((code) =>
    selectedTypes.includes(code)
  ).length;
  const allTypesSelected = selectedTypes.length === LIBRARY_GROUP_ORDER.length;
  const someTypesSelected = selectedTypes.length > 0 && !allTypesSelected;
  const allServicesSelected =
    selectedServiceCount === SERVICE_TYPE_CODES.length;
  const someServicesSelected = selectedServiceCount > 0 && !allServicesSelected;
  const translate = (key, fallback, options = {}) =>
    t(key, { defaultValue: fallback, ...options });

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const toggleType = (code) => {
    onChange(
      selectedTypes.includes(code)
        ? selectedTypes.filter((selectedCode) => selectedCode !== code)
        : [...selectedTypes, code]
    );
  };

  const toggleAllServices = () => {
    const contentSelection = selectedTypes.filter(
      (code) => !SERVICE_TYPE_CODES.includes(code)
    );
    onChange(
      allServicesSelected
        ? contentSelection
        : [...contentSelection, ...SERVICE_TYPE_CODES]
    );
  };

  const toggleAllTypes = () => {
    onChange(allTypesSelected ? [] : [...LIBRARY_GROUP_ORDER]);
  };

  const getTriggerLabel = () => {
    if (allTypesSelected) {
      return translate('allTypes', 'All types');
    }
    if (selectedTypes.length === 0) {
      return translate('noTypesSelected', 'No types selected');
    }
    if (
      selectedTypes.length === SERVICE_TYPE_CODES.length &&
      allServicesSelected
    ) {
      return translate('allServices', 'All services');
    }
    if (selectedTypes.length === 1) {
      const contentType = CONTENT_TYPES.find(
        ({ code }) => code === selectedTypes[0]
      );
      if (contentType) {
        return translate(contentType.translationKey, contentType.fallbackLabel);
      }
      const serviceType = SERVICE_TYPES.find(
        ({ code }) => code === selectedTypes[0]
      );
      return serviceType
        ? translate(`serviceType${serviceType.name}`, serviceType.name)
        : translate('selectedTypeCount', '{{count}} types', {
            count: selectedTypes.length
          });
    }
    return translate('selectedTypeCount', '{{count}} types', {
      count: selectedTypes.length
    });
  };

  const filterLabel = translate('filterByType', 'Filter library by type');

  return (
    <div className='library-type-filter-menu' ref={containerRef}>
      <button
        type='button'
        className={`library-type-filter-menu__trigger${isOpen ? ' library-type-filter-menu__trigger--open' : ''}${!allTypesSelected ? ' library-type-filter-menu__trigger--selected' : ''}`}
        aria-label={`${filterLabel}: ${getTriggerLabel()}`}
        aria-haspopup='true'
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>{getTriggerLabel()}</span>
        {isOpen ? (
          <CaretUp size={16} aria-hidden='true' />
        ) : (
          <CaretDown size={16} aria-hidden='true' />
        )}
      </button>

      {isOpen && (
        <div
          className='library-type-filter-menu__popover'
          role='group'
          aria-label={filterLabel}
        >
          <button
            type='button'
            className='library-type-filter-menu__option'
            aria-pressed={someTypesSelected ? 'mixed' : allTypesSelected}
            onClick={toggleAllTypes}
          >
            <FilterCheckbox
              checked={allTypesSelected}
              indeterminate={someTypesSelected}
            />
            <span
              className='library-type-filter-menu__icon-spacer'
              aria-hidden='true'
            />
            <span className='library-type-filter-menu__label'>
              {translate('allTypes', 'All types')}
            </span>
            <span className='library-type-filter-menu__count'>
              {counts.total}
            </span>
          </button>

          <div className='library-type-filter-menu__divider' />

          {CONTENT_TYPES.map(
            ({ code, translationKey, fallbackLabel, Icon }) => (
              <button
                type='button'
                className='library-type-filter-menu__option'
                aria-pressed={selectedTypes.includes(code)}
                onClick={() => toggleType(code)}
                key={code}
              >
                <FilterCheckbox checked={selectedTypes.includes(code)} />
                <Icon
                  className='library-type-filter-menu__icon'
                  size={16}
                  aria-hidden='true'
                />
                <span className='library-type-filter-menu__label'>
                  {translate(translationKey, fallbackLabel)}
                </span>
                <span className='library-type-filter-menu__count'>
                  {counts[code] || 0}
                </span>
              </button>
            )
          )}

          <div className='library-type-filter-menu__divider' />

          <button
            type='button'
            className='library-type-filter-menu__option'
            aria-pressed={someServicesSelected ? 'mixed' : allServicesSelected}
            onClick={toggleAllServices}
          >
            <FilterCheckbox
              checked={allServicesSelected}
              indeterminate={someServicesSelected}
            />
            <span
              className='library-type-filter-menu__icon-spacer'
              aria-hidden='true'
            />
            <span className='library-type-filter-menu__label'>
              {translate('allServices', 'All services')}
            </span>
            <span className='library-type-filter-menu__count'>
              {counts.services}
            </span>
          </button>

          {SERVICE_TYPES.map(({ code, name, iconref }) => {
            const Icon = PhosphorIcons[iconref] || PhosphorIcons.Gear;
            return (
              <button
                type='button'
                className='library-type-filter-menu__option library-type-filter-menu__option--service'
                aria-pressed={selectedTypes.includes(code)}
                onClick={() => toggleType(code)}
                key={code}
              >
                <FilterCheckbox checked={selectedTypes.includes(code)} />
                <Icon
                  className='library-type-filter-menu__icon'
                  size={16}
                  aria-hidden='true'
                />
                <span className='library-type-filter-menu__label'>
                  {translate(`serviceType${name}`, name)}
                </span>
                <span className='library-type-filter-menu__count'>
                  {counts[code] || 0}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

LibraryTypeFilter.propTypes = {
  counts: PropTypes.shape({
    total: PropTypes.number.isRequired,
    services: PropTypes.number.isRequired
  }).isRequired,
  selectedTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired
};

export default LibraryTypeFilter;
